import { Link } from 'react-router-dom';
import PublicLayout from './PublicLayout';
import { DownloadLink } from './download';
import useFetch from './useFetch';
import { useState } from 'react';
import { getPublic, fmtTime, fmtDate, panna, hasResult } from './api';

const SUPPORT_PHONE = ''; // e.g. '918058819237'

function MarketCard({ m }) {
  // A market is bettable until its open cutoff passes; after close it is done.
  const openLive = !m.stop_open_bid;
  const closeLive = !m.stop_close_bid;
  const live = openLive || closeLive;

  return (
    <div className="card mkt">
      <div className="top">
        <span className="nm">{m.game_name}</span>
        <span className={`pill ${live ? 'open' : 'shut'}`}>
          <span className="dot" />{live ? 'Open' : 'Closed'}
        </span>
      </div>
      {hasResult(m.open_pana, m.open_digit) || hasResult(m.close_pana, m.close_digit) ? (
        <div className="num">
          {panna(m.open_pana, m.open_digit) || '—'}
          <span style={{ color: 'var(--muted)', margin: '0 6px' }}>·</span>
          {panna(m.close_pana, m.close_digit) || '—'}
        </div>
      ) : (
        <div className="num await" style={{ fontSize: 14 }}>
          {live ? 'Betting open · result awaited' : 'Result awaited'}
        </div>
      )}
      <div className="tm">
        <span>Open {fmtTime(m.open_time)}</span>
        <span>Close {fmtTime(m.close_time)}</span>
      </div>
    </div>
  );
}


function PhonePreview({ markets, loading }) {
  // Shows the real markets, not mock numbers, so the preview always matches
  // what the app is actually showing today.
  const rows = markets.slice(0, 4);

  return (
    <div className="phone" aria-label="App preview">
      <div className="phone-scr">
        <div className="phone-bar">
          <span>VIP Kalyan</span>
          <span className="phone-bal">Wallet ₹ ••••</span>
        </div>
        <div className="phone-list">
          {loading || rows.length === 0
            ? [0, 1, 2, 3].map((i) => (
                <div key={i} className="prow" style={{ height: 62, background: '#e9eef5' }} />
              ))
            : rows.map((m) => {
                const live = !m.stop_open_bid || !m.stop_close_bid;
                return (
                  <div className="prow" key={m.game_name}>
                    <div className="r1">
                      <span className="nm2">{m.game_name}</span>
                      <span className={`st ${live ? 'o' : 'c'}`}>{live ? 'Open' : 'Closed'}</span>
                    </div>
                    <div className="n2">
                      {hasResult(m.open_pana, m.open_digit) || hasResult(m.close_pana, m.close_digit)
                        ? `${panna(m.open_pana, m.open_digit) || '—'} · ${panna(m.close_pana, m.close_digit) || '—'}`
                        : '— · —'}
                    </div>
                    <div className="t2">
                      Open {fmtTime(m.open_time)} · Close {fmtTime(m.close_time)}
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [showAll, setShowAll] = useState(false);
  const { data: markets, loading: mLoading } = useFetch(() => getPublic('/markets'), []);
  const { data: results, loading: rLoading } = useFetch(() => getPublic('/results', { limit: 8 }), []);
  const { data: rates } = useFetch(() => getPublic('/rates'), []);

  const liveCount = markets.filter((m) => !m.stop_open_bid || !m.stop_close_bid).length;
  const topRate = rates.reduce((max, r) => Math.max(max, Number(r.winning_points) || 0), 0);

  return (
    <PublicLayout
      title="Live Matka Results, Rates & Winners"
      description="VIP Kalyan — live results for Kalyan, Milan, Rajdhani, Main Bazar and more, plus game rates, daily winners and how to play."
    >
      {/* hero */}
      <section className="wrap page" style={{ paddingBottom: 26 }}>
        <div className="hero-grid">
          <div>
        <span className="eyebrow">● Live results, every day</span>
        <h1 style={{ marginTop: 12 }}>
          Today&apos;s markets,{' '}
          <span style={{ background: 'linear-gradient(90deg,var(--gold-2),var(--gold))', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            results and rates
          </span>
        </h1>
        <p className="lede">
          Kalyan, Milan, Rajdhani, Main Bazar and more — with open and close numbers as they are declared,
          the full rate card, and the day&apos;s winners.
        </p>
        <div className="hero-cta">
          <DownloadLink className="btn btn-hero">
            <span className="dl-ic">↓</span> Download App
          </DownloadLink>
          <span className="hero-sub">Android · Free · 18+ only</span>
        </div>
        <div style={{ display: 'flex', gap: 34, flexWrap: 'wrap', marginTop: 26 }}>
          <div><b style={{ display: 'block', fontSize: 25, color: 'var(--gold)' }}>{markets.length || '—'}</b><span style={{ fontSize: 13, color: 'var(--muted)' }}>Markets</span></div>
          <div><b style={{ display: 'block', fontSize: 25, color: 'var(--green)' }}>{mLoading ? '—' : liveCount}</b><span style={{ fontSize: 13, color: 'var(--muted)' }}>Open now</span></div>
          <div><b style={{ display: 'block', fontSize: 25, color: 'var(--gold)' }}>{topRate ? topRate.toLocaleString('en-IN') : '—'}</b><span style={{ fontSize: 13, color: 'var(--muted)' }}>Top rate</span></div>
        </div>
          </div>

          <div>
            <PhonePreview markets={markets} loading={mLoading} />
            <p className="cap">Live markets, exactly as they appear in the app</p>
          </div>
        </div>
      </section>

      {/* markets */}
      <section className="wrap" style={{ paddingBottom: 40 }}>
        <div className="sec-hd">
          <h2 style={{ margin: 0 }}>Markets</h2>
          <Link to="/results">All results →</Link>
        </div>
        {mLoading ? (
          <div className="grid g3">{[0,1,2,3,4,5].map((i) => <div key={i} className="skel" />)}</div>
        ) : markets.length ? (
          <>
            <div className="grid g3">
              {(showAll ? markets : markets.slice(0, 6)).map((m) => (
                <MarketCard key={m.game_name} m={m} />
              ))}
            </div>
            {markets.length > 6 && !showAll && (
              <button type="button" className="more" onClick={() => setShowAll(true)}>
                Show all {markets.length} markets
              </button>
            )}
          </>
        ) : (
          <div className="card empty">No markets available right now.</div>
        )}
      </section>

      {/* latest results */}
      <section className="wrap" style={{ paddingBottom: 40 }}>
        <div className="sec-hd">
          <h2 style={{ margin: 0 }}>Latest results</h2>
          <Link to="/results">See all →</Link>
        </div>
        {rLoading ? (
          <div className="skel" style={{ height: 220 }} />
        ) : results.length ? (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Market</th><th>Date</th><th>Open</th><th>Close</th></tr></thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={`${r.game_name}-${i}`}>
                    <td data-label="Market" style={{ fontWeight: 700 }}>{r.game_name}</td>
                    <td data-label="Date" style={{ color: 'var(--muted)' }}>{fmtDate(r.date)}</td>
                    <td data-label="Open" className={hasResult(r.open_panna, r.open_digit) ? 'mono' : 'await'}>
                      {hasResult(r.open_panna, r.open_digit) ? panna(r.open_panna, r.open_digit) : 'Awaited'}
                    </td>
                    <td data-label="Close" className={hasResult(r.close_panna, r.close_digit) ? 'mono' : 'await'}>
                      {hasResult(r.close_panna, r.close_digit) ? panna(r.close_panna, r.close_digit) : 'Awaited'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card empty">No results declared yet.</div>
        )}
      </section>

      {/* rates teaser */}
      <section className="wrap" style={{ paddingBottom: 50 }}>
        <div className="sec-hd">
          <h2 style={{ margin: 0 }}>Game rates</h2>
          <Link to="/rates">Full rate card →</Link>
        </div>
        <div className="grid g4">
          {rates.slice(0, 4).map((r) => (
            <div key={r.game_type} className="card mkt">
              <span className="nm">{r.game_type}</span>
              <div className="num" style={{ fontSize: 19 }}>
                {r.bid_points} → {Number(r.winning_points).toLocaleString('en-IN')}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* app */}
      <section className="wrap" style={{ paddingBottom: 30 }}>
        <div className="card" style={{ padding: '34px 28px', textAlign: 'center' }}>
          <span className="eyebrow">Get the app</span>
          <h2 style={{ marginTop: 8 }}>VIP Kalyan for Android</h2>
          <p className="lede" style={{ margin: '0 auto 20px' }}>
            Place bids, track your wallet and see results on your phone.
          </p>

          <DownloadLink className="btn" style={{ fontSize: 16, padding: '14px 30px' }}>
            ↓ Download for Android
          </DownloadLink>
          <p className="soon" style={{ marginTop: 16 }}>
            Free · Android 5.0 and above · 38&nbsp;MB. Android will ask you to allow installs from
            unknown sources the first time — that is normal for an app installed outside the Play Store.
          </p>
          {SUPPORT_PHONE ? (
            <p className="soon" style={{ marginTop: 14 }}>
              Need help? <a href={`https://wa.me/${SUPPORT_PHONE}`}>Contact support on WhatsApp</a>
            </p>
          ) : null}
        </div>
      </section>
    </PublicLayout>
  );
}
